(ns frontend.view.settings2.e-signature.view
  (:require [clojure.edn :as edn]
            [common.localization :as loc]
            [common.util :as cu]
            [frontend.db :as app-db]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.view.components.clipboard :as clipboard]
            [frontend.view.modals.components.processing-modal :as processing-modal]
            [frontend.view.modals.events :as modals]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.e-signature.events :as events]
            [frontend.view.settings2.e-signature.sub :as sub]
            [frontend.view.settings2.sales-pipelines.db :as db]
            [oops.core :as oops]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.color :as c]
            [ui.components.breadcrumbs.view :as breadcrumbs]
            [ui.components.button.view :as button]
            [ui.components.link.view :as link]
            [ui.components.modal.view :as modal]
            [ui.components.radix-ui.tooltip.view :as tooltip]
            [ui.components.select.select :as select]
            [ui.dom-globals :as dg]
            [ui.editors.labelled-input.view :as labelled-input]))

(defn get-webhook-url
  [webhook-id]
  (str @app-db/server-url "/esign/api/webhook/" @session/tenant-id "/" webhook-id))

;; After for different environments must be configured in docusign app dashboard,
;; like https://app.zapflow.com/settings/e-signature, https://staging.zapflow.xyz/settings/e-signature etc.
(defn get-docusign-after-consent-redirect-url
  []
  (str @app-db/server-url "/settings/e-signature"))

(defn get-docusign-consent-redirect-url
  []
  (let [docusign-base-url (if (session/prod?)
                            "https://account.docusign.com/oauth/auth"
                            "https://account-d.docusign.com/oauth/auth")
        integration-key (:docusign-integration-key @app-db/app-state)
        after-consent-redirect-url (get-docusign-after-consent-redirect-url)
        consent-redirect-url (str
                              docusign-base-url
                              "?response_type=code&scope=signature%20impersonation&client_id="
                              integration-key
                              "&redirect_uri="
                              after-consent-redirect-url)]
    consent-redirect-url))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/e-signature)]])

(defn pill-component
  [i entity-type entity-id remove-disabled?]
  ^{:key (gensym "pill")}
  [:div
   {:class "py-2 pl-3 pr-2 m-0 text-sm border border-solid border-base-light-border-paler rounded-full"}
   [:span
    {:class "flex gap-2 items-center"}
    [:div
     [:div (loc/tr entity-type)]
     [:div
      {:class "font-bold leading-3"}
      (->> @session/pipelines
           (filter
            (fn [pipeline]
              (= entity-id (:db/id pipeline))))
           (first)
           :pipeline/name)]]
    (when-not remove-disabled?
      [:div {:on-click (fn [] (rf/dispatch [::events/remove-target i]))}
       [:i.fas.fa-times-circle
        {:class "cursor-pointer hover:text-base-light-text-accent"}]])]])

(defn target-pills
  [targets remove-disabled?]
  [:<>
   (->> targets
        (map-indexed (fn [i target]
                       ^{:key i}
                       [pill-component i (:entity/type target) (:db/id target) remove-disabled?])))])

(defn webhook-copy-button
  [_]
  (let [copied?* (r/atom false)]
    (fn [{:keys [webhook-id]}]
      (let [copied? @copied?*]
        (if copied?
          [:div
           {:class "flex items-center gap-1 select-none"}
           [:p (loc/tr :copied)]
           [:i {:class "fal fa-check font-medium"}]]
          [button/secondary-open
           {:on-click (fn [e]
                        (clipboard/copy-to (get-webhook-url webhook-id))
                        (dg/prevent-and-stop e)
                        (reset! copied?* true)
                        (js/setTimeout #(reset! copied?* false) 1500))
            :tooltip  (loc/tr :copy-webhook-url-to-clipboard)
            :class    "py-0"}
           [:span (loc/tr :esign-webhook-url) [:i.fal.fa-copy]]])))))

(defn credential-row
  [{:keys [i name webhook-id targets provider] :as credential}
   first-credential-of-email-and-provider?]
  [:div
   {:class "mb-4 flex flex-col gap-4"}
   [:div
    {:class "p-3 border border-solid border-complement-regular-border-paler rounded"}

    [:div
     [:div {:class "flex justify-between items-center"}
      [:span
       {:class "font-bold"}
       name]
      [:div
       {:class "flex items-center gap-3"}
       (if first-credential-of-email-and-provider?
         [webhook-copy-button
          {:webhook-id webhook-id}]
         [tooltip/view
          [:div
           [webhook-copy-button
            {:webhook-id webhook-id
             :disabled?  true}]]
          [:div "Only one webhook per " (loc/tr provider) " account should be configured"]])
       [button/square-secondary-hollow
        {:on-click (fn []
                     (rf/dispatch-sync [::events/set-edited-credential {:credential credential
                                                                        :provider   provider}])
                     (rf/dispatch [::events/set-edited-credential-index i])
                     (modals/open-modal!
                      :esign-settings-modal
                      {:index    i
                       :provider provider}))}
        [:i.fal.fa-pencil.edit-icon]]
       [button/square-secondary-hollow
        {:on-click (fn []
                     (rf/dispatch [::events/remove-credentials i]))}
        [:i.fas.fa-trash-alt]]]]
     [:div {:class "py-1 flex flex-wrap gap-2"}
      [target-pills targets true]]]]])

(defonce modal-key :esign-settings-modal)

(defn webhook-url-field
  [_webhook-id]
  (let [copied?* (r/atom false)]
    (fn [webhook-id]
      (let [copied? @copied?*
            webhook-url @(r/track get-webhook-url webhook-id)]
        [:div
         [:label
          {:class "text-[14px] text-input-light-text font-semibold"}
          (loc/tr :esign-webhook-url)]
         [:div
          {:class "flex items-center gap-3"}
          [:div
           {:class "break-all text-sm m-0"}
           webhook-url]
          (if copied?
            [:div
             {:class "w-9 h-6 flex items-center justify-center"}
             [:i {:class "fal fa-check font-medium"}]]
            [button/square-secondary-hollow
             {:tooltip   (loc/tr :copy-webhook-url-to-clipboard)
              :on-click  (fn [_]
                           (clipboard/copy-to webhook-url)
                           (reset! copied?* true)
                           (js/setTimeout #(reset! copied?* false) 1500))
              :disabled? copied?}
             [:i {:class "fal fa-copy"}]])]]))))

(defn target-config
  [targets credential-index]
  (let [target-entity-type @(rf/subscribe [::sub/target-entity-type])
        target-entity-id @(rf/subscribe [::sub/target-entity-id])
        target-input-visible? @(rf/subscribe [::sub/target-input-visible?])
        options (concat
                 [{:value :entity.type/pipeline
                   :label (loc/tr :entity.type/pipeline)}
                  {:value :entity.type/sale-agreement
                   :label (loc/tr :entity.type/sale-agreement)}
                  {:value      :entity.type/kyc-process
                   :label      "KYC"
                   :isDisabled (-> (map :entity/type targets)
                                   (set)
                                   (contains? :entity.type/kyc-process))}]
                 (when (session/counterparty-diligence?)
                   [{:value :entity.type/counterparty-diligence-process
                     :label "Counterparty diligence"}]))]
    (if target-input-visible?
      [:div
       {:class "flex gap-2 items-center mt-2"}
       [:div
        {:class "w-2/5"}
        [select/view
         {:id          "esign-target-entity-type-select"
          :value       (->> options
                            (cu/filter-by :value target-entity-type)
                            (first))
          :placeholder (str (loc/tr :esign-choose-entity) " -> " target-entity-type)
          :on-change   (fn [value _value-opt]
                         (rf/dispatch [::events/set-target-entity-type (edn/read-string value)]))
          :options     (->> options
                            (map (fn [opt]
                                   (assoc opt :value (pr-str (:value opt))))))}]]
       (when (= target-entity-type :entity.type/pipeline)
         [:div
          {:class "w-2/5"}
          [select/view
           {:id                 "esign-target-entity-id-select"
            :value              (when target-entity-id
                                  {:value target-entity-id
                                   :label (case target-entity-type
                                            :entity.type/pipeline (->> @session/pipelines
                                                                       (filter
                                                                        (fn [pipeline]
                                                                          (= (:db/id pipeline) target-entity-id)))
                                                                       first
                                                                       :pipeline/name))})
            :menu-portal-target (dg/element-by-id "react-select-menu-container")
            :placeholder        (loc/tr :esign-choose-pipeline)
            :on-change          (fn [value]
                                  (rf/dispatch [::events/set-target-entity-id value]))
            :options            (->> @session/pipelines
                                     (filter (fn [pipeline]
                                               (not (some #(= (:db/id pipeline) %)
                                                          (->> targets
                                                               (map :entity/type))))))
                                     (map
                                      (fn [pipeline]
                                        {:value (:db/id pipeline)
                                         :label (:pipeline/name pipeline)})))}]])
       [button/square-primary-solid
        {:disabled? (nil? target-entity-type)
         :on-click  (fn []
                      (rf/dispatch [::events/set-target-input-visible false])
                      (if (= target-entity-type :entity.type/kyc-process)
                        (rf/dispatch [::events/add-target credential-index {:entity/type :entity.type/kyc-process}])
                        (rf/dispatch [::events/add-target credential-index
                                      {:entity/type :entity.type/pipeline
                                       :db/id       target-entity-id}])))}
        [:i.fas.fa-save]]
       [button/square-danger-solid
        {:on-click (fn [] (rf/dispatch [::events/set-target-input-visible false]))}
        [:i.fas.fa-trash]]]
      [:div
       [button/sm-secondary-hollow
        {:on-click (fn [] (rf/dispatch [::events/set-target-input-visible true]))}
        [:span [:i.fal.fa-plus] " Add"]]])))

(defn oneflow-view-body
  [{:keys [index]}]
  (let [visible-token-rows @(rf/subscribe [::sub/visible-token-rows])
        token-visible? (contains? visible-token-rows index)
        {:keys [name email token webhook-sign-key oneflow-workspace-id oneflow-template-id targets webhook-id]} @(rf/subscribe [::sub/edited-credential])]
    [:div
     [:div
      {:class "flex w-full gap-4"}
      [:div
       {:class "flex-1 px-3 pt-3 pb-6 -mt-3 flex flex-col gap-3 grow overflow-hidden"}
       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :esign-connection-name)
         :value     name
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :name value]))}]

       [:div
        {:class "relative"}
        [labelled-input/view
         {:data-type (if token-visible?
                       :data.type/text-simple
                       :data.type/password)
          :label     (loc/tr :esign-api-token)
          :value     token
          :on-change (fn [value]
                       (rf/dispatch [::events/set-edited-credential-field index :token value]))}]

        (if token-visible?
          [:i.fal.fa-eye-slash
           {:class    "absolute right-2 top-8 cursor-pointer"
            :on-click (fn [] (rf/dispatch [::events/hide-token index]))}]
          [:i.fal.fa-eye
           {:class    "absolute right-2 top-8 cursor-pointer"
            :on-click (fn [] (rf/dispatch [::events/show-token index]))}])]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :email)
         :value     email
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :email value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :oneflow-workspace-id)
         :value     oneflow-workspace-id
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :oneflow-workspace-id value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :oneflow-template-id)
         :value     oneflow-template-id
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :oneflow-template-id value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :esign-webhook-key)
         :value     webhook-sign-key
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :webhook-sign-key value]))}]

       [webhook-url-field webhook-id]]
      [:div {:class "mb-0 py-4 px-3 -mt-6 -mr-6 w-5/12 hidden md:flex gap-3 flex-col bg-base-regular text-base-light-text text-sm shrink-0"}
       [:p
        (loc/tr :oneflow-intro1)]
       [:p
        (loc/tr :oneflow-intro2)]
       [:p
        (loc/tr :oneflow-intro3)]
       [:a
        {:class  "text-base-light-text-accent underline"
         :href   "https://oneflow.com/product-tour/"
         :target "_blank"} (loc/tr :more-information)]]]
     [:div {:class "flex flex-col gap-2 pt-3 border-t border-solid border-base-light-border-paler"}
      [:p (loc/tr :esign-use-credentials)]
      [:div
       {:class "flex gap-2 flex-wrap items-center"}
       [target-pills targets false]]
      [target-config targets index]]]))

(defn docusign-view-body
  [{:keys [index]}]
  (let [{:keys [name docusign-user-id docusign-account-id docusign-connect-key targets webhook-id]} @(rf/subscribe [::sub/edited-credential])]
    [:div
     [:div
      {:class "flex w-full gap-4"}
      [:div
       {:class "flex-1 px-3 pt-3 pb-6 -mt-3 flex flex-col gap-3"}
       [:div
        [button/sm-primary-solid
         {:on-click (fn []
                      (let [url (get-docusign-consent-redirect-url)]
                        (oops/ocall js/window "open" url "_blank")))}
         (str "Grant Zapflow access to DocuSign")]]
       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :esign-connection-name)
         :value     name
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :name value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :docusign-user-id)
         :value     docusign-user-id
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :docusign-user-id value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :docusign-connect-key)
         :value     docusign-connect-key
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :docusign-connect-key value]))}]

       [labelled-input/view
        {:data-type :data.type/text-simple
         :label     (loc/tr :docusign-account-id)
         :value     docusign-account-id
         :on-change (fn [value]
                      (rf/dispatch [::events/set-edited-credential-field index :docusign-account-id value]))}]

       [webhook-url-field webhook-id]]
      [:div
       {:class "py-4 px-3 mb-0 -mt-6 -mr-6 w-5/12 hidden md:flex gap-3 flex-col bg-base-regular text-base-light-text text-sm"}
       [:p
        (loc/tr :docusign-intro1)]
       [:a
        {:class  "hover:text-base-light-text-accent"
         :href   "https://www.docusign.com/products/electronic-signature"
         :target "_blank"} (loc/tr :more-information)]]]
     [:div {:class "pt-4 flex flex-col gap-2 border-t border-solid border-base-light-border-paler"}
      [:p (loc/tr :esign-use-credentials)]
      [:div {:class "flex gap-3 w-full"}
       [target-pills targets false]]
      [target-config targets index]]]))

(defn footer-view
  [{:keys [on-close! close-modal!]}]
  [:<>
   [modal/left-cancel-only
    (fn [_]
      (on-close!)) nil]

   ;; TODO: Maybe implement checking if the configuration is valid later?
   #_[button/sm-secondary-hollow
      {}
      "Check credential validity"]

   [modal/right-single-button
    nil
    false
    (fn []
      (rf/dispatch [::events/save-all-credentials])
      (close-modal!))
    "Confirm"
    nil]])

(defn view
  [{:keys [state shown? close-modal! props topmost?]}]
  (let [{:keys [index credentials provider on-show! edit?]} props
        on-close! (fn []
                    (rf/dispatch [::events/set-edited-credential {:credential nil}])
                    (rf/dispatch [::events/set-edited-credential-index nil])
                    (close-modal!))]
    [processing-modal/render-modal
     {:state             state
      :shown?            (and shown? topmost?)
      :success-text      (loc/tr :success-guest-invited)
      :on-show!          (when (fn? on-show!) on-show!)
      :on-close!         on-close!
      :class-name        "overflow-initial"
      :header-color      ::c/bg-2
      :default-title-fn  (fn [] (str (loc/tr :esign-connect-with) " " (loc/tr provider)))
      :default-body-fn   (case provider
                           :oneflow (partial oneflow-view-body
                                             {:state       state
                                              :index       index
                                              :credentials credentials
                                              :edit?       edit?})
                           :docusign (partial docusign-view-body
                                              {:state       state
                                               :index       index
                                               :credentials credentials
                                               :edit?       edit?}))
      :default-footer-fn (partial footer-view
                                  {:state        state
                                   :index        index
                                   :on-close!    on-close!
                                   :close-modal! close-modal!})}]))

;;WARNING - TIMO, MIKO - check logic here
(defn provider-button
  [provider]
  (let [selected-provider (or @(rf/subscribe [::sub/selected-provider]) provider)
        active? (= provider selected-provider)]
    [link/side-nav-link
     {:active?  active?
      :on-click (fn [] (rf/dispatch [::events/set-provider provider]))}
     (loc/tr provider)]))

(defn general-configuration
  []
  (let [credentials @(rf/subscribe [::sub/credentials])
        new-credentials-index (count credentials)
        selected-provider (or @(rf/subscribe [::sub/selected-provider]) :oneflow)
        provider-credentials (->> credentials
                                  (map-indexed
                                   (fn [i c]
                                     [i c]))
                                  (filter
                                   (fn [[_credential-index credential]]
                                     (= selected-provider (:provider credential)))))]
    [:div
     [:div
      {:class "flex flex-col gap-4"}
      [:h2 {:class "text-xl font-bold"}
       (loc/tr :settings.configuration/e-signature)]
      [:div
       [:label
        (loc/tr :esign-settings-info)]
       [:div {:class "mt-5 grid grid-cols-1 md:grid-cols-10 gap-4"}
        [:div
         {:class "flex md:flex-col md:col-span-2 gap-3"}
         [provider-button :oneflow]
         [provider-button :docusign]]
        (if-not (empty? provider-credentials)
          [:div
           {:class "md:col-span-8"}
           (->> provider-credentials
                (map-indexed
                 (fn
                   [provider-credential-index
                    [credential-index
                     {:keys [email name token webhook-id webhook-sign-key oneflow-workspace-id oneflow-template-id docusign-user-id docusign-account-id
                             docusign-connect-key provider
                             targets]}]]
                   (let [first-credential-of-email-and-provider? (case provider
                                                                   :oneflow (let [previous-oneflow-emails (->> provider-credentials
                                                                                                               (take provider-credential-index)
                                                                                                               (map second)
                                                                                                               (map :email)
                                                                                                               (set))]
                                                                              (-> previous-oneflow-emails
                                                                                  (contains? email)
                                                                                  (not)))
                                                                   :docusign (let [previous-docusign-account-ids (->> provider-credentials
                                                                                                                      (take provider-credential-index)
                                                                                                                      (map second)
                                                                                                                      (map :docusign-account-id)
                                                                                                                      (set))]
                                                                               (-> previous-docusign-account-ids
                                                                                   (contains? docusign-account-id)
                                                                                   (not))))]
                     ^{:key credential-index}
                     [credential-row
                      {:i                    credential-index
                       :email                email
                       :name                 name
                       :token                token
                       :webhook-id           webhook-id
                       :webhook-sign-key     webhook-sign-key
                       :docusign-connect-key docusign-connect-key
                       :oneflow-workspace-id oneflow-workspace-id
                       :oneflow-template-id  oneflow-template-id
                       :docusign-user-id     docusign-user-id
                       :docusign-account-id  docusign-account-id
                       :targets              targets
                       :provider             selected-provider}
                      first-credential-of-email-and-provider?]))))
           [button/sm-primary-solid
            {:on-click (fn []
                         (rf/dispatch-sync [::events/set-edited-credential {:credential {}
                                                                            :add-new?   true
                                                                            :provider   selected-provider}])
                         (modals/open-modal!
                          :esign-settings-modal
                          {:index       new-credentials-index
                           :credentials credentials
                           :provider    selected-provider
                           :on-show!    (fn [])}))}
            (str "+ New " (loc/tr selected-provider) " connection")]]
          [:div {:class "col-span-8"}
           [:div {:class "p-4 flex flex-col justify-center items-center gap-8"}
            [:img
             {:src   "/img/rfp-email.png"
              :class "w-48"}]
            [button/primary-solid
             {:on-click (fn []
                          (rf/dispatch-sync [::events/set-edited-credential {:credential {}
                                                                             :add-new?   true
                                                                             :provider   selected-provider}])
                          (modals/open-modal!
                           :esign-settings-modal
                           {:index       new-credentials-index
                            :credentials credentials
                            :provider    selected-provider
                            :on-show!    (fn [])}))}
             (str "+ " (loc/tr :esign-connect-with) " " (loc/tr selected-provider))]]])]]]]))

(defn toolbar-buttons
  []
  [])

(defn component
  []
  (r/create-class
   {:component-did-mount    (fn []
                              (rf/dispatch [::events/set-all-credentials (get @session/tenant :tenant/esign-api-credentials)])
                              (rf/dispatch [::events/set-provider :oneflow]))
    :component-will-unmount (fn [] (rf/dispatch [::events/set-all-credentials nil]))
    :reagent-render
    (fn []
      [:<>
       [zap-toolbar/view db/state breadcrumbs nil (toolbar-buttons)]
       [:div
        {:class "h-[calc(100vh_-_52px-_37px)] overflow-auto"}
        [:div
         {:class "px-4 py-6 lg:mx-auto lg:w-5xl"}
         [general-configuration]]]])}))

(defmethod routing/render-view :settings/e-signature
  [_]
  (if (session/has-feature-access? :feature/entity-esign)
    [default-page-layout/view
     [component]]
    [not-found/not-found-view]))
